import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
const COLORS = {
  primary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  dark: '#1e293b',
  fab: '#8b5cf6',
  install: '#0ea5e9',
};

const getProgressColor = (val) =>
  val >= 100 ? COLORS.success : val >= 75 ? '#22c55e' : val >= 50 ? COLORS.warning : val > 0 ? '#fb923c' : '#cbd5e1';

const fmtNum = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

const ProgressBar = ({ value, color, height = 18 }) => (
  <div style={{
    width: '100%', backgroundColor: '#f1f5f9', borderRadius: height / 2,
    height, overflow: 'hidden', position: 'relative',
  }}>
    <div style={{
      width: `${Math.min(Math.max(value, 0), 100)}%`, height: '100%',
      backgroundColor: color || getProgressColor(value),
      borderRadius: height / 2, transition: 'width 0.6s ease-out',
    }} />
    <span style={{
      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      fontSize: '10px', fontWeight: '700', color: value > 45 ? '#fff' : '#475569',
      textShadow: value > 45 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
    }}>
      {Math.round(value * 10) / 10}%
    </span>
  </div>
);

const PipingSOVAnalytics = ({ projectId, asOf = null }) => {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('sov_code');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (projectId) fetchData();
  }, [projectId, asOf]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/piping-sov-analytics/project/${projectId}${asOf ? `?asOf=${asOf}` : ''}`
      );
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching piping SOV analytics:', err);
      setError('Failed to load piping SOV analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = useCallback((field) => {
    setSortDir(prev => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortField(field);
  }, [sortField]);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') {
        if (av === 'Unassigned') return 1;
        if (bv === 'Unassigned') return -1;
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === 'asc' ? (av - bv) : (bv - av);
    });
  }, [data, sortField, sortDir]);

  const thStyle = {
    padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0',
    fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase',
    whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
  };
  const tdStyle = {
    padding: '10px 12px', borderBottom: '1px solid #f3f4f6', textAlign: 'center', fontSize: '13px',
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>{'\u2195'}</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: '3px solid #e2e8f0', borderTop: `3px solid ${COLORS.primary}`,
            borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ color: '#64748b', fontSize: '14px' }}>Loading SOV analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>!</div>
        <div style={{ color: COLORS.danger, fontWeight: '600' }}>{error}</div>
        <button onClick={fetchData} style={{
          marginTop: 12, padding: '8px 20px', backgroundColor: COLORS.primary, color: '#fff',
          border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: '600',
        }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
        No SOV data available for this project.
      </div>
    );
  }

  const { totals } = data;

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: COLORS.dark }}>
            SOV Progress by Fabrication & Installation
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
            Schedule of Values progress breakdown
          </p>
        </div>
        <button onClick={fetchData} style={{
          padding: '8px 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0',
          borderRadius: 8, cursor: 'pointer', fontSize: '13px', color: '#475569', fontWeight: '500',
        }}>
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.info}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Total Credit</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{fmtNum(totals.credit)}</div>
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.fab}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Earned Fab</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{fmtNum(totals.earned_credit_fab)}</div>
          <div style={{ fontSize: '0.75rem', color: COLORS.fab, fontWeight: '600' }}>{totals.pct_fab}%</div>
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.install}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Earned Install</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{fmtNum(totals.earned_credit_install)}</div>
          <div style={{ fontSize: '0.75rem', color: COLORS.install, fontWeight: '600' }}>{totals.pct_install}%</div>
        </div>
        <div style={{ backgroundColor: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${COLORS.success}` }}>
          <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500' }}>Overall Progress</div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: COLORS.dark }}>{totals.pct_total}%</div>
          <ProgressBar value={totals.pct_total} height={10} />
        </div>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => handleSort('sov_code')}>
                  SOV Code <SortIcon field="sov_code" />
                </th>
                <th style={{ ...thStyle, textAlign: 'left', minWidth: 140 }} onClick={() => handleSort('sov_description')}>
                  Description <SortIcon field="sov_description" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#fefce8' }} onClick={() => handleSort('credit')}>
                  Credit <SortIcon field="credit" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f5f3ff' }} onClick={() => handleSort('earned_credit_fab')}>
                  Earned Credit Fab <SortIcon field="earned_credit_fab" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f5f3ff' }} onClick={() => handleSort('pct_fab')}>
                  % Fab <SortIcon field="pct_fab" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#eff6ff' }} onClick={() => handleSort('earned_credit_install')}>
                  Earned Credit Install <SortIcon field="earned_credit_install" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#eff6ff' }} onClick={() => handleSort('pct_install')}>
                  % Install <SortIcon field="pct_install" />
                </th>
                <th style={{ ...thStyle, backgroundColor: '#f0fdf4' }} onClick={() => handleSort('pct_total')}>
                  % Total <SortIcon field="pct_total" />
                </th>
                <th style={{ ...thStyle, width: 180 }}>Progress</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, idx) => (
                <tr key={r.sov_code} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600', color: r.sov_code === 'Unassigned' ? '#94a3b8' : COLORS.dark }}>
                    {r.sov_code}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'left', color: '#64748b', fontSize: '12px' }}>
                    {r.sov_description || '—'}
                  </td>
                  <td style={{ ...tdStyle, backgroundColor: idx % 2 === 0 ? '#fffef5' : '#fefce8', fontWeight: '600' }}>
                    {fmtNum(r.credit)}
                  </td>
                  <td style={{ ...tdStyle, backgroundColor: idx % 2 === 0 ? '#faf8ff' : '#f5f3ff' }}>
                    {fmtNum(r.earned_credit_fab)}
                  </td>
                  <td style={{ ...tdStyle, backgroundColor: idx % 2 === 0 ? '#faf8ff' : '#f5f3ff', fontWeight: '600', color: getProgressColor(r.pct_fab) }}>
                    {r.pct_fab}%
                  </td>
                  <td style={{ ...tdStyle, backgroundColor: idx % 2 === 0 ? '#f8fbff' : '#eff6ff' }}>
                    {fmtNum(r.earned_credit_install)}
                  </td>
                  <td style={{ ...tdStyle, backgroundColor: idx % 2 === 0 ? '#f8fbff' : '#eff6ff', fontWeight: '600', color: getProgressColor(r.pct_install) }}>
                    {r.pct_install}%
                  </td>
                  <td style={{ ...tdStyle, fontWeight: '700', color: getProgressColor(r.pct_total) }}>
                    {r.pct_total}%
                  </td>
                  <td style={{ ...tdStyle, paddingLeft: 8, paddingRight: 8 }}>
                    <ProgressBar value={r.pct_total} height={18} />
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ backgroundColor: '#1e293b' }}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '700', color: '#fff', borderBottom: 'none' }}>
                  Grand Total
                </td>
                <td style={{ ...tdStyle, color: '#94a3b8', borderBottom: 'none', fontSize: '11px' }}>
                  {data.rows.length} SOV codes
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#fff', borderBottom: 'none' }}>
                  {fmtNum(totals.credit)}
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#c4b5fd', borderBottom: 'none' }}>
                  {fmtNum(totals.earned_credit_fab)}
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#c4b5fd', borderBottom: 'none' }}>
                  {totals.pct_fab}%
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#7dd3fc', borderBottom: 'none' }}>
                  {fmtNum(totals.earned_credit_install)}
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#7dd3fc', borderBottom: 'none' }}>
                  {totals.pct_install}%
                </td>
                <td style={{ ...tdStyle, fontWeight: '700', color: '#86efac', borderBottom: 'none' }}>
                  {totals.pct_total}%
                </td>
                <td style={{ ...tdStyle, paddingLeft: 8, paddingRight: 8, borderBottom: 'none' }}>
                  <div style={{
                    width: '100%', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 9,
                    height: 18, overflow: 'hidden', position: 'relative',
                  }}>
                    <div style={{
                      width: `${Math.min(totals.pct_total, 100)}%`, height: '100%',
                      backgroundColor: COLORS.success, borderRadius: 9,
                      transition: 'width 0.6s ease-out',
                    }} />
                    <span style={{
                      position: 'absolute', top: '50%', left: '50%',
                      transform: 'translate(-50%, -50%)', fontSize: '10px',
                      fontWeight: '700', color: '#fff',
                    }}>
                      {totals.pct_total}%
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Formula note */}
      <div style={{ marginTop: '0.75rem', fontSize: '11px', color: '#94a3b8', textAlign: 'right' }}>
        % Total = (Earned Credit Fab + Earned Credit Install) / (Credit x 2)
      </div>
    </div>
  );
};

export default PipingSOVAnalytics;
